import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';

export interface InspectorPest {
  id: string;
  code: string;
  name: string;
  actionThreshold: number;
}

export interface PointInspectorProps {
  incPests: InspectorPest[];
  currentPoint: number;
  cellState: (point: number, pestId: string) => 'empty' | 'clean' | 'infested';
  onSetVerdict: (point: number, pestId: string, verdict: 'clean' | 'infested') => void;
  onMarkClean?: (point: number) => void;
  onAttachPhoto: (file: File) => void;
  onPrev: () => void;
  onNext: () => void;
  onNextEmpty: () => void;
}

export function PointInspector({
  incPests,
  currentPoint,
  cellState,
  onSetVerdict,
  onMarkClean,
  onAttachPhoto,
  onPrev,
  onNext,
  onNextEmpty,
}: PointInspectorProps) {
  const { t } = useTranslation();
  const fileRef = useRef<HTMLInputElement>(null);

  const treeNum = Math.floor(currentPoint / 10) + 1;
  const pointNum = (currentPoint % 10) + 1;
  const pointLabel = `P-${String(currentPoint + 1).padStart(3, '0')}`;

  return (
    <div className="insp">
      {/* Header */}
      <div className="insp__head" style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
        <span className="insp__pid" style={{ fontWeight: 900, fontSize: '20px', letterSpacing: '-0.01em' }}>
          {pointLabel}
        </span>
      </div>
      <div className="insp__sub" style={{ fontSize: '12px', color: 'var(--fg-muted)', fontFamily: 'monospace', marginBottom: '12px' }}>
        {t('fieldScout.desktop.point.treePoint', { n: treeNum, k: pointNum })}
      </div>

      {/* Section label */}
      <div className="section-label" style={{ fontSize: '11px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--fg-muted)', marginBottom: '8px' }}>
        {t('fieldScout.desktop.point.thisPoint')}
      </div>

      {/* Verdict rows */}
      <div>
        {incPests.map(pest => {
          const state = cellState(currentPoint, pest.id);
          return (
            <div
              key={pest.id}
              style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 0', borderBottom: '1px solid var(--neutral-200)' }}
            >
              <span style={{ fontWeight: 900, fontSize: '14px', width: '42px', color: 'var(--blue-800)' }}>
                {pest.code}
              </span>
              <span style={{ fontSize: '12px', color: 'var(--fg-muted)', flex: 1 }}>
                {pest.name}
              </span>
              <span className="toggle" style={{ display: 'inline-flex', border: '2px solid var(--neutral-300)', borderRadius: '8px', overflow: 'hidden' }}>
                <button
                  data-testid={`verdict-${pest.id}-clean`}
                  className={`toggle__btn${state === 'clean' ? ' on clean' : ''}`}
                  style={{
                    font: 'inherit',
                    fontSize: '11px',
                    fontWeight: 800,
                    textTransform: 'uppercase',
                    padding: '6px 10px',
                    border: 0,
                    background: state === 'clean' ? 'var(--green-500)' : '#fff',
                    color: state === 'clean' ? '#fff' : 'var(--neutral-600)',
                    cursor: 'pointer',
                  }}
                  onClick={() => onSetVerdict(currentPoint, pest.id, 'clean')}
                >
                  {t('fieldScout.desktop.point.clean')}
                </button>
                <button
                  data-testid={`verdict-${pest.id}-infested`}
                  className={`toggle__btn${state === 'infested' ? ' on inf' : ''}`}
                  style={{
                    font: 'inherit',
                    fontSize: '11px',
                    fontWeight: 800,
                    textTransform: 'uppercase',
                    padding: '6px 10px',
                    border: 0,
                    background: state === 'infested' ? 'var(--red-500)' : '#fff',
                    color: state === 'infested' ? '#fff' : 'var(--neutral-600)',
                    cursor: 'pointer',
                  }}
                  onClick={() => onSetVerdict(currentPoint, pest.id, 'infested')}
                >
                  {t('fieldScout.desktop.point.infested')}
                </button>
              </span>
            </div>
          );
        })}
      </div>

      {/* Mark the whole point examined with every pest clean */}
      {onMarkClean && (
        <button
          type="button"
          data-testid="mark-all-clean"
          style={{ width: '100%', marginTop: '12px', border: '1px solid var(--green-500)', background: '#fff', borderRadius: '10px', padding: '9px', fontWeight: 700, fontSize: '13px', color: 'var(--green-700)', cursor: 'pointer' }}
          onClick={() => onMarkClean(currentPoint)}
        >
          {t('fieldScout.desktop.markAllClean')}
        </button>
      )}

      {/* Attach photo */}
      <button
        className="photo-btn"
        style={{ width: '100%', marginTop: '12px', border: '2px dashed var(--neutral-300)', background: 'var(--neutral-50)', borderRadius: '10px', padding: '10px', fontWeight: 700, fontSize: '13px', color: 'var(--neutral-600)', cursor: 'pointer' }}
        onClick={() => fileRef.current?.click()}
      >
        {t('fieldScout.desktop.point.attachPhoto')}
      </button>
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) onAttachPhoto(file);
        }}
      />

      {/* Navigation */}
      <div className="insp__navrow" style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
        <button
          className="btn btn-outline-secondary"
          style={{ flex: 1, justifyContent: 'center' }}
          onClick={onPrev}
        >
          {t('fieldScout.desktop.point.prev')}
        </button>
        <button
          className="btn btn-outline-secondary"
          data-testid="next-empty"
          style={{ flex: 1, justifyContent: 'center' }}
          onClick={onNextEmpty}
        >
          {t('fieldScout.desktop.point.nextEmpty')}
        </button>
        <button
          className="btn btn-primary"
          style={{ flex: 1, justifyContent: 'center' }}
          onClick={onNext}
        >
          {t('fieldScout.desktop.point.next')}
        </button>
      </div>
    </div>
  );
}

export default PointInspector;
